import React, { useEffect, useState } from 'react';
import { Modal } from '../design/Modal';
import { Button } from '../design/Button';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import { Loader2, Ban, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { can } from '@/utils/permissions';

interface BannedPlayer {
  uuid: string;
  name: string;
  created: string;
  source: string;
  expires: string;
  reason: string;
}

interface BannedPlayersModalProps {
  isOpen: boolean;
  onClose: () => void;
  serverId: string;
}

export const BannedPlayersModal: React.FC<BannedPlayersModalProps> = ({
  isOpen,
  onClose,
  serverId,
}) => {
  const [bannedPlayers, setBannedPlayers] = useState<BannedPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const canUnban = can(userStore.currentUser, 'can_moderate_server');

  const loadBannedPlayers = async () => {
    setLoading(true);
    const players = await serverStore.fetchBannedPlayers(serverId);
    setBannedPlayers(players);
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      loadBannedPlayers();
    }
  }, [isOpen, serverId]);

  const handleUnban = async (playerName: string) => {
    setActionLoading(playerName);
    const res = await serverStore.executePlayerAction(serverId, 'unban', playerName);
    if (res.success) {
      toast.success(`${playerName} erfolgreich entbannt`);
      await loadBannedPlayers();
    } else {
      toast.error(res.message || 'Entbannen fehlgeschlagen');
    }
    setActionLoading(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gebannte Spieler" size="lg">
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="animate-spin text-blue-500" size={32} />
        </div>
      ) : bannedPlayers.length === 0 ? (
        <div className="text-center py-12 text-fg-muted text-sm flex flex-col items-center">
          <Ban size={32} className="mb-3 opacity-20 text-red-500" />
          Keine gebannten Spieler vorhanden
        </div>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 custom-scrollbar">
          {bannedPlayers.map((player) => (
            <div
              key={player.uuid || player.name}
              className="bg-black/20 border border-line rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all hover:border-line-strong"
            >
              <div className="flex items-start space-x-3">
                <img
                  src={`https://mc-heads.net/avatar/${player.name}/32`}
                  alt={player.name}
                  className="w-10 h-10 rounded-lg shadow-md flex-shrink-0"
                />
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm tracking-wide">
                      {player.name}
                    </span>
                    <span className="text-[10px] text-fg-muted font-mono bg-white/5 px-1.5 py-0.5 rounded">
                      {player.expires === 'forever' ? 'Dauerhaft' : `Ablauf: ${player.expires}`}
                    </span>
                  </div>
                  <p className="text-xs text-fg-muted">
                    <span className="text-fg-muted font-medium">Grund:</span> {player.reason || 'Kein Grund angegeben'}
                  </p>
                  <p className="text-[10px] text-fg-muted">
                    Gebannt von: {player.source} am {player.created}
                  </p>
                </div>
              </div>

              {canUnban && (
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full sm:w-auto bg-green-600 hover:bg-green-500 border-green-700/50 flex items-center justify-center gap-2"
                  disabled={actionLoading === player.name}
                  onClick={() => handleUnban(player.name)}
                >
                  {actionLoading === player.name ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <UserCheck size={14} />
                  )}
                  Entbannen
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
